import React from 'react';
import RefreshIcon from '@mui/icons-material/Refresh';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {CircularProgress, IconButton, Paper, Stack, Typography} from '@mui/material';
import {formatTime} from '../utils/formatTimestamp';

/**
 * OwnMessageBubble
 *
 * Shared light-grey bubble shell for the current user's own messages.
 * Used in both AI assistant chat and human team chat to ensure visual consistency.
 *
 * The text content is passed as children so each feature keeps its own renderer
 * (ReactMarkdown for AI chat, plain Typography for human chat).
 *
 * Props:
 *   children        — message content (ReactMarkdown, Typography, image, etc.)
 *   timestamp       — date value understood by formatTime (epoch, ISO, Firestore Timestamp)
 *   isPending       — show sending spinner
 *   isStreaming     — show streaming spinner
 *   isError         — show error icon
 *   canRetry        — show retry button
 *   onRetry()       — called when retry is clicked
 *   message         — full message object passed back to onRetry
 */
const OwnMessageBubble = ({
    children,
    timestamp,
    isPending = false,
    isStreaming = false,
    isError = false,
    canRetry = false,
    onRetry,
    message,
}) => (
    <Stack alignItems="flex-end">
        <Paper
            elevation={0}
            sx={{
                px: 1.5,
                py: 1,
                maxWidth: '80%',
                borderRadius: 2,
                bgcolor: 'grey.100',
            }}
        >
            <Stack spacing={0.5}>
                {children}

                <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                    {(isPending || isStreaming) && (
                        <CircularProgress size={14} aria-label="Sending"/>
                    )}
                    {isError && <ErrorOutlineIcon color="error" fontSize="small"/>}
                    {timestamp && (
                        <Typography variant="caption" color="text.secondary">
                            {formatTime(timestamp)}
                        </Typography>
                    )}
                </Stack>

                {canRetry && (
                    <Stack direction="row" justifyContent="flex-end">
                        <IconButton
                            size="small"
                            onClick={() => onRetry?.(message)}
                            aria-label="Retry message"
                        >
                            <RefreshIcon fontSize="small"/>
                        </IconButton>
                    </Stack>
                )}
            </Stack>
        </Paper>
    </Stack>
);

export default OwnMessageBubble;
